// R-131-FIN-DECISION-K — build report citation evidence from a pinned PathwayComparison.

import {
  formatDecisionRecommendationSummary,
  recommendPathwayComparison,
} from '../../finDecision/decisionRecommendation';
import {
  computeAllPathwayCompareMetrics,
  formatUsdCompactMillions,
  formatUsdPerMwh,
} from '../../finDecision/pathwayCompareMetrics';
import type { PathwayComparisonDocument, PathwayComparisonAtGen } from '../../../types/pathwayComparison';
import type { ProjectIntent } from '../../../types/siteRag';
import { buildPathwayComparisonViewPath } from '../../../services/pathwayComparisonFirestore';

export interface PathwayComparisonEvidence {
  comparisonId: string;
  name: string;
  updatedAt: string;
  citationSourceId: string;
  viewPathwaysUrl: string;
  decisionRecommendation: string;
  pathways: PathwayComparisonAtGen['pathways'];
  atGen: PathwayComparisonAtGen;
}

export function buildPathwayComparisonEvidence(
  doc: PathwayComparisonDocument,
  opts?: {
    carbonCommitment?: ProjectIntent['carbonCommitment'];
    carbonPricePerTUsd?: number;
    now?: Date;
  },
): PathwayComparisonEvidence {
  const recommendation = recommendPathwayComparison(doc.pathways, {
    now: opts?.now,
    carbonCommitment: opts?.carbonCommitment,
    carbonPricePerTUsd: opts?.carbonPricePerTUsd,
  });
  const metrics = computeAllPathwayCompareMetrics(doc.pathways, {
    now: opts?.now,
    carbonCommitment: opts?.carbonCommitment,
    carbonPricePerTUsd: opts?.carbonPricePerTUsd,
  });

  // Join each metric row back to its source Pathway to carry the corrected
  // classification (grid / btm / hybrid) + technology onto the snapshot, so BOTH
  // the Pathway Comparison report AND the Financial report can show a distinct
  // Type/Source column from the SAME persisted artifact.
  const pathwayById = new Map(doc.pathways.map((p) => [p.id, p]));
  const pathwaySnapshots = metrics.map((row) => {
    const src = pathwayById.get(row.pathwayId);
    return {
      pathwayId: row.pathwayId,
      name: row.name,
      headlineLabel: row.headline.label,
      lcoeUsdPerMwh: row.lcoeRealUsdPerMwh,
      npvUsd: row.npvUsd,
      ...(src ? { classification: src.classification, technology: src.technology } : {}),
    };
  });

  const atGen: PathwayComparisonAtGen = {
    comparisonId: doc.id,
    name: doc.name,
    updatedAt: doc.updatedAt,
    pathways: pathwaySnapshots,
    decisionRecommendation: formatDecisionRecommendationSummary(recommendation),
    viewPathwaysUrl: buildPathwayComparisonViewPath(doc.id),
    recommendationConfidence: recommendation.confidence,
    recommendationPrimaryReason: recommendation.primaryReason,
  };

  return {
    comparisonId: doc.id,
    name: doc.name,
    updatedAt: doc.updatedAt,
    citationSourceId: `pathwayComparison:${doc.id}`,
    viewPathwaysUrl: atGen.viewPathwaysUrl,
    decisionRecommendation: atGen.decisionRecommendation,
    pathways: pathwaySnapshots,
    atGen,
  };
}

/** Human-readable footnote for report appendix / pathway_comparison section. */
export function buildPathwayComparisonFootnote(evidence: PathwayComparisonEvidence): string {
  const rows = evidence.pathways
    .map(
      (row) =>
        `${row.name}: ${row.headlineLabel}, LCOE ${formatUsdPerMwh(row.lcoeUsdPerMwh)}, NPV ${formatUsdCompactMillions(row.npvUsd)}`,
    )
    .join('; ');
  return (
    `${evidence.decisionRecommendation} ` +
    `(${rows}). ` +
    `Source: pathway comparison ${evidence.comparisonId} (${evidence.updatedAt}). ` +
    `View pathways: ${evidence.viewPathwaysUrl}`
  );
}
